import { useEffect, useRef, useCallback } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import {
  konamiActiveAtom, pizzaDayAtom, milestoneAtom, isMutedAtom,
  satsGameAtom, satsScoreAtom,
} from '../state/atoms';
import { playMilestoneSound } from '../utils/audio';

const KONAMI_SEQUENCE = [
  'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
  'KeyB', 'KeyA',
];

const SEQUENCES = {
  pizza: ['KeyP', 'KeyI', 'KeyZ', 'KeyZ', 'KeyA'],
  fire: ['KeyF', 'KeyI', 'KeyR', 'KeyE'],
  sats: ['KeyS', 'KeyA', 'KeyT', 'KeyS'],
};

const SEQUENCE_TIMEOUT = 3000;

export const useEasterEggs = (resetCameraRef) => {
  const setKonami = useSetRecoilState(konamiActiveAtom);
  const setPizzaDay = useSetRecoilState(pizzaDayAtom);
  const setMilestone = useSetRecoilState(milestoneAtom);
  const isMuted = useRecoilValue(isMutedAtom);
  const setSatsGame = useSetRecoilState(satsGameAtom);
  const setSatsScore = useSetRecoilState(satsScoreAtom);

  const konamiIdx = useRef(0);
  const sequenceBuffers = useRef({
    pizza: 0,
    fire: 0,
    sats: 0,
  });
  const lastKeyTime = useRef(0);
  const tapTimestamps = useRef([]);

  // Check for Pizza Day on mount
  useEffect(() => {
    const now = new Date();
    if (now.getMonth() === 4 && now.getDate() === 22) {
      setPizzaDay(true);
    }
  }, [setPizzaDay]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const now = Date.now();
      if (now - lastKeyTime.current > SEQUENCE_TIMEOUT) {
        konamiIdx.current = 0;
        Object.keys(sequenceBuffers.current).forEach(k => { sequenceBuffers.current[k] = 0; });
      }
      lastKeyTime.current = now;

      // Konami code
      if (e.code === KONAMI_SEQUENCE[konamiIdx.current]) {
        // Suppress arrow keys from moving camera during sequence
        if (e.code.startsWith('Arrow')) e.preventDefault();
        konamiIdx.current++;
        if (konamiIdx.current >= KONAMI_SEQUENCE.length) {
          konamiIdx.current = 0;
          setKonami(true);
          setTimeout(() => setKonami(false), 10000);
        }
      } else if (e.code === KONAMI_SEQUENCE[0]) {
        konamiIdx.current = 1;
      } else {
        konamiIdx.current = 0;
      }

      // Word sequences — suppress movement keys during active entry
      Object.entries(SEQUENCES).forEach(([name, seq]) => {
        const idx = sequenceBuffers.current[name];
        // If we're mid-sequence (past first key), suppress the key from flight controls
        if (idx > 0 && e.code === seq[idx]) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
        if (e.code === seq[idx]) {
          sequenceBuffers.current[name]++;
          if (sequenceBuffers.current[name] >= seq.length) {
            sequenceBuffers.current[name] = 0;
            // Reset camera smoothly after any sequence
            if (resetCameraRef?.current) resetCameraRef.current();
            switch (name) {
              case 'pizza':
                setPizzaDay(p => !p);
                break;
              case 'fire':
                if (!isMuted) playMilestoneSound();
                setMilestone(true);
                setTimeout(() => setMilestone(false), 4000);
                break;
              case 'sats':
                setSatsScore(0);
                setSatsGame(g => !g);
                break;
              default:
                break;
            }
          }
        } else if (e.code === seq[0]) {
          sequenceBuffers.current[name] = 1;
        } else {
          sequenceBuffers.current[name] = 0;
        }
      });
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [setKonami, setPizzaDay, setMilestone, isMuted, resetCameraRef, setSatsGame, setSatsScore]);

  // Mobile Konami: 10 rapid taps on BTC logo
  const handleLogoTap = useCallback(() => {
    const now = Date.now();
    const taps = tapTimestamps.current;
    taps.push(now);
    // Keep only taps within last 3 seconds
    while (taps.length > 0 && now - taps[0] > 3000) taps.shift();
    if (taps.length >= 10) {
      taps.length = 0;
      setKonami(true);
      setTimeout(() => setKonami(false), 5000);
    }
  }, [setKonami]);

  return { handleLogoTap };
};
